import React from 'react'

/**
 * Wrap every occurrence of the search tokens within ``text`` in a <mark> so a
 * list/table can show operators exactly which part of a row matched what they
 * typed - WPS-48 (highlight search tokens in results).
 *
 * The query is split on whitespace and each token is matched case-insensitively
 * as a substring (the same semantics the backend ``icontains`` search uses), so
 * a query like "89 Main St" lights up "89", "Main", and "St" wherever they
 * appear. Spaces, hyphens and dots inside a match are ignored, as the backend
 * does for postcodes, so "t4c1k6" lights up "T4C 1K6".
 */
export function HighlightMatch({
  text,
  query,
}: {
  text: string
  query: string
}): React.ReactNode {
  if (!query || !text) return text

  const tokens = Array.from(
    new Set(
      query
        .split(/\s+/)
        .map((token) => token.trim())
        .filter(Boolean),
    ),
  )
  // Prefer tokens of length >= 2 so a stray single character does not paint the
  // whole table; fall back to the raw tokens when the query is itself 1 char.
  const meaningful = tokens.filter((token) => token.length >= 2)
  const finalTokens = (meaningful.length ? meaningful : tokens)
    // Longest first so a longer token wins over a shorter one it contains.
    .sort((a, b) => b.length - a.length)
  if (finalTokens.length === 0) return text

  const pattern = finalTokens.map(tokenPattern).join('|')
  // One capturing group, so split() puts every match at an odd index.
  const parts = text.split(new RegExp(`(${pattern})`, 'gi'))

  return parts.map((part, index) =>
    part && index % 2 === 1 ? (
      <mark
        key={index}
        className="rounded-[2px] bg-[var(--gold-bg-muted)] px-[1px] text-[var(--gold-ink)]"
      >
        {part}
      </mark>
    ) : (
      part
    ),
  )
}

// The token's characters with any spaces, hyphens or dots allowed between them.
function tokenPattern(token: string): string {
  const chars = Array.from(token.replace(/[\s.-]+/g, ''))
  return (chars.length ? chars : Array.from(token)).map(escapeRegExp).join('[\\s.-]*')
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
